import { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation, useNavigate } from 'react-router';

import { useAppDispatch } from '@/app/store/hooks';
import { getUserId, markSessionVerified } from '@/shared/lib/tokenStorage';

import { useRequestOtpMutation, useVerifyOtpMutation } from './auth.api';
import { otpCodeSchema } from './auth.schema';
import { authenticated } from './auth.slice';
import { isLockoutMessage, readOtpFailure, type AgentContact } from './otpFailure';
import { useCountdown } from './useCountdown';

/** The legacy portal's wait before a resend when the server names none. */
const DEFAULT_RESEND_SECONDS = 60;

const WRONG_CODE_MESSAGE = 'That code was not correct. Check your messages and try again.';

/** Handed over by `useLinkLogin` when it routes here. */
interface OtpRouteState {
  asicMemberId?: string;
  nextRoute?: string;
  otpExpiry?: number;
}

/** A send that did not go through. Separate from `codeError` because it is
 *  about the SMS, not about anything the director typed. */
export interface SendProblem {
  message: string;
  agentContact: AgentContact | null;
}

interface OtpVerification {
  code: string;
  setCode: (code: string) => void;
  /** True until the first send settles. That reply can say no code is needed
   *  at all, so there is nothing for the director to act on before it lands. */
  isArriving: boolean;
  notice: string | null;
  codeError: string | null;
  sendProblem: SendProblem | null;
  /** Out of attempts. Only the agent can restore access, so no resend. */
  isLocked: boolean;
  resendInSeconds: number;
  isVerifying: boolean;
  isResending: boolean;
  /** Null when the page was opened directly rather than reached from login. */
  asicMemberId: string | null;
  submit: () => Promise<void>;
  resend: () => Promise<void>;
}

export const useOtpVerification = (): OtpVerification => {
  const location = useLocation();
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const [verifyOtp, { isLoading: isVerifying }] = useVerifyOtpMutation();
  const [requestOtp, { isLoading: isResending }] = useRequestOtpMutation();
  const { secondsLeft: resendInSeconds, start: startCooldown } = useCountdown();

  const [code, setCode] = useState('');
  const [isArriving, setIsArriving] = useState(true);
  const [notice, setNotice] = useState<string | null>(null);
  const [codeError, setCodeError] = useState<string | null>(null);
  const [sendProblem, setSendProblem] = useState<SendProblem | null>(null);
  const [isLocked, setIsLocked] = useState(false);

  const state = (location.state ?? {}) as OtpRouteState;
  // Falls back to storage so a refresh on the OTP screen does not strand the
  // director: router state does not survive a reload, but the login did store
  // the member id.
  const asicMemberId = state.asicMemberId ?? getUserId();
  const nextRoute = state.nextRoute ?? '/';

  // Only here does the session become usable — the token from login alone
  // does not clear the route guard. The marker persists that fact so a reload
  // does not drop back behind the second factor.
  const enter = useCallback((): void => {
    markSessionVerified();
    dispatch(authenticated());
    void navigate(nextRoute, { replace: true });
  }, [dispatch, navigate, nextRoute]);

  const send = useCallback(
    async (memberId: string, forceResend: boolean): Promise<void> => {
      setNotice(null);
      setSendProblem(null);

      try {
        const result = await requestOtp({ asicMemberId: memberId, forceResend }).unwrap();
        if (result.otpVerified) {
          enter();
          return;
        }
        startCooldown(result.retryAfterSec ?? DEFAULT_RESEND_SECONDS);
        setNotice(
          result.message ??
            (forceResend
              ? 'A new verification code has been sent.'
              : 'A verification code has been sent.'),
        );
      } catch (error) {
        const failure = readOtpFailure(error);
        if (failure.isThrottled) {
          startCooldown(failure.retryAfterSec ?? DEFAULT_RESEND_SECONDS);
          setIsLocked(isLockoutMessage(failure.message));
        }
        setSendProblem({
          message:
            failure.message ??
            (failure.isThrottled
              ? 'A code was sent recently. Please wait before requesting another.'
              : 'We could not send a code just now. Please try again.'),
          agentContact: failure.agentContact,
        });
      }
    },
    [requestOtp, enter, startCooldown],
  );

  // Once per visit, with `forceResend: false` so the server can decline to
  // text again while a code is still live. StrictMode runs effects twice in
  // development, and a second send would be a second SMS.
  const arrived = useRef(false);

  useEffect(() => {
    if (asicMemberId === null || arrived.current) return;
    arrived.current = true;

    void send(asicMemberId, false).finally(() => {
      setIsArriving(false);
    });
  }, [asicMemberId, send]);

  const rejectCode = (message: string | null): void => {
    setCodeError(message ?? WRONG_CODE_MESSAGE);
    setIsLocked(isLockoutMessage(message));
    setCode('');
  };

  const submit = async (): Promise<void> => {
    if (asicMemberId === null) return;

    const parsed = otpCodeSchema.safeParse(code);
    if (!parsed.success) {
      setCodeError(parsed.error.issues[0]?.message ?? 'Enter the code from your message.');
      return;
    }

    setCodeError(null);
    try {
      const result = await verifyOtp({ asicMemberId, inputCode: parsed.data }).unwrap();
      if (result.success) {
        enter();
        return;
      }
      rejectCode(result.error ?? null);
    } catch (error) {
      rejectCode(readOtpFailure(error).message);
    }
  };

  const resend = async (): Promise<void> => {
    if (asicMemberId === null || resendInSeconds > 0) return;
    setCodeError(null);
    await send(asicMemberId, true);
  };

  return {
    code,
    setCode,
    isArriving,
    notice,
    codeError,
    sendProblem,
    isLocked,
    resendInSeconds,
    isVerifying,
    isResending,
    asicMemberId,
    submit,
    resend,
  };
};
